import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { Workspace } from '../../types'

interface WorkspaceModalProps {
  isOpen: boolean
  workspace?: Workspace | null
  onClose: () => void
  onSave: (data: { name: string; description?: string; isShared: boolean; isDefault: boolean }) => void
  isAdmin: boolean
}

export function WorkspaceModal({ isOpen, workspace, onClose, onSave, isAdmin }: WorkspaceModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isShared, setIsShared] = useState(false)
  const [isDefault, setIsDefault] = useState(false)

  useEffect(() => {
    if (workspace) {
      setName(workspace.name)
      setDescription(workspace.description || '')
      setIsShared(workspace.isShared)
      setIsDefault(workspace.isDefault)
    } else {
      setName('')
      setDescription('')
      setIsShared(false)
      setIsDefault(false)
    }
  }, [workspace, isOpen])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      isShared,
      isDefault,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {workspace ? t('workspaces.edit') : t('workspaces.create')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
            aria-label={t('common.cancel')}
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="ws-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('workspaces.name')}
            </label>
            <input
              id="ws-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 border dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm"
              placeholder={t('workspaces.namePlaceholder')}
              required
              autoFocus
              maxLength={200}
            />
          </div>
          <div>
            <label htmlFor="ws-desc" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('workspaces.description')}
            </label>
            <textarea
              id="ws-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-2 border dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm"
              placeholder={t('workspaces.descriptionPlaceholder')}
              rows={2}
              maxLength={500}
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              {t('workspaces.setDefault')}
            </label>
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={isShared}
                  onChange={e => setIsShared(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                {t('workspaces.shared')}
              </label>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
